import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { BQXConfiguration } from 'bqx-ui-common';
import * as i2 from 'rxjs';
import { ReplaySubject, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

const authClientCredentials = new InjectionToken('authClientCredentials');
class BqxFeatureSwitchesModule {
    static forRoot(authToken) {
        return {
            ngModule: BqxFeatureSwitchesModule,
            providers: [
                { provide: authClientCredentials, useValue: authToken }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxFeatureSwitchesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: BqxFeatureSwitchesModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxFeatureSwitchesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxFeatureSwitchesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

var VariantKey;
(function (VariantKey) {
    VariantKey["TRUE"] = "true";
    VariantKey["FALSE"] = "false";
    VariantKey["VARIANT_A"] = "variant_a";
    VariantKey["VARIANT_B"] = "variant_b";
    VariantKey["VARIANT_C"] = "variant_c";
    VariantKey["VARIANT_D"] = "variant_d";
    VariantKey["EMPTY_STRING"] = "";
})(VariantKey || (VariantKey = {}));

class AbstractFlagrService {
}

class FlagrService {
    get isFlagrRCalled$() {
        return this._isFlagrRCalled$.asObservable();
    }
    constructor(http, authToken) {
        this.http = http;
        this.authToken = authToken;
        this._isFlagrRCalled$ = new ReplaySubject();
        this.authToken.subscribe((tokenValue) => {
            this._accessToken = tokenValue;
        });
    }
    callFlagr(quoteNumber, flagKeys, ssid, constraints) {
        let response;
        // make the API call to flagr API
        return this.http
            .post(BQXConfiguration.data.flagrBaseUrl, this.generateBody(quoteNumber, flagKeys, ssid, constraints), {
            headers: this.generateHeaders()
        })
            .pipe(switchMap((results) => {
            // store API call results
            this.storeFlagrResult(results, flagKeys);
            response = {
                success: true,
                result: results
            };
            return of(response);
        }), catchError((err) => {
            this.storeDefaultFlagrResults(flagKeys);
            response = {
                success: false,
                error: err
            };
            return throwError(() => {
                return response;
            });
        }));
    }
    /**
     * @description will return the value of a specific flagr key
     * @param flagDescription should be passed without the 'flagr_' prefix. The value should be passed how it is typed in the config file
     * @returns the value of the flagr value for a specific flagr key or null if it was not found
     */
    getFlagrValue(flagDescription) {
        const flagName = 'flagr_' + flagDescription;
        return sessionStorage.getItem(flagName);
    }
    /**
     * @description will set a variable to know if flagr has been called or not
     * @param
     * @returns void
     */
    setFlagrCalled() {
        sessionStorage.setItem('flagr_called', VariantKey.TRUE);
        this._isFlagrRCalled$.next(true);
    }
    /**
     * @description will get a variable to know if flagr has been called or not
     * @param
     * @returns true it it has been called or false if it hasn't
     */
    get hasFlagrBeenCalled() {
        return sessionStorage.getItem('flagr_called') === VariantKey.TRUE;
    }
    storeFlagrResult(result, flagKeys) {
        const flagResult = result.results;
        flagKeys.forEach((flagItem) => {
            const flag = flagResult.find((flag) => flag.flagKey === flagItem.flagKey);
            if (flag?.flagKey !== '' && flag?.variantKey !== VariantKey.EMPTY_STRING) {
                sessionStorage.setItem('flagr_' + flagItem.flagDescription, flag.variantKey);
            }
            else {
                sessionStorage.setItem('flagr_' + flagItem.flagDescription, flagItem.defaultValue);
            }
        });
    }
    storeDefaultFlagrResults(flagKeys) {
        flagKeys.forEach((flagItem) => {
            sessionStorage.setItem('flagr_' + flagItem.flagDescription, flagItem.defaultValue);
        });
    }
    generateHeaders() {
        const headers = new HttpHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + this._accessToken
        });
        return headers;
    }
    generateBody(quoteNumber, flagKeys, ssid, constraints) {
        const temp = {
            quoteNumber: quoteNumber,
            ssid: ssid,
            flags: flagKeys,
            constraints: {
                state: constraints?.state,
                businessClass: constraints?.businessClass,
                day: constraints.day,
                date: constraints.date,
                time: constraints.time
            }
        };
        const body = JSON.stringify(temp);
        return body;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FlagrService, deps: [{ token: i1.HttpClient }, { token: authClientCredentials }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FlagrService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FlagrService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: i2.BehaviorSubject, decorators: [{
                    type: Inject,
                    args: [authClientCredentials]
                }] }] });

/*
 * Public API Surface of bqx-feature-switches
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractFlagrService, BqxFeatureSwitchesModule, FlagrService, VariantKey, authClientCredentials };
